import type { PermissionCode } from '@/auth/AuthContext';
import type { ReleasesResponse, WorkCounts } from '@/api/orders';
import { releasesPending } from '@/features/orders/releasesLib';

/** Everything a queue count can be computed from. */
export interface QueueSources {
  work: WorkCounts;
  releases: ReleasesResponse | null;
}

/**
 * The one description of what this app contains: every nav row, the route it
 * goes to, and the permissions that decide whether the user sees it at all.
 *
 * Two kinds of entry, kept strictly apart:
 *   WORK — dedicated action views. Each is a queue with a count taken from the
 *          server's `work` object, so navigation can never disagree with the
 *          view it opens.
 *   JOURNEYS — pure status browsing on the main list (All + stage filters).
 *
 * Permission codes are the ones in docs/PERMISSIONS.md: a PAGE code per view
 * (frontend-enforced — the server has no pages) plus, for the views made of
 * money, the READ code the server requires for their data, so a hidden row
 * and a 403 can't come apart.
 */
export type NavGroup = 'JOURNEYS' | 'WORK' | 'TOOLS';

/** Every id a route, a nav row or an overview card can be keyed by. */
export type ViewId =
  | 'shell'
  | 'overview'
  | 'today'
  | 'orders'
  | 'actions'
  | 'to_order'
  | 'setup'
  | 'releases'
  | 'decide'
  | 'move'
  | 'documents'
  | 'balance'
  | 'duties'
  | 'prices'
  | 'followups'
  | 'packages'
  | 'warehouse'
  | 'print_count_sheet';

/**
 * THE page table. The router, the shell nav and the overview all read this
 * and nothing else, so a route and its nav row cannot drift apart — there is
 * no second place to state a code. One page code per view; the shell itself
 * needs the base read every Orders user holds, because it loads the journey
 * list and the overview counts for every page.
 *
 * Where a view holds controls needing a WRITE code, that control carries its
 * own `<Can code>` at the call site with the code the server route requires.
 */
export const VIEW_PERMISSIONS: Record<ViewId, PermissionCode> = {
  shell: 'orders:read:journeys',
  overview: 'orders:page:overview',
  // Today is the front door to the same asks To decide lists, so it opens
  // with that page's code; its read ranks by money (orders:read:costs below).
  today: 'orders:page:actions',
  orders: 'orders:page:journeys',
  actions: 'orders:page:actions',
  to_order: 'orders:page:to_order',
  setup: 'orders:page:setup',
  releases: 'orders:page:releases',
  decide: 'orders:page:discrepancies',
  move: 'orders:page:move',
  documents: 'orders:page:documents',
  balance: 'orders:page:balance',
  duties: 'orders:page:duties',
  prices: 'orders:page:prices',
  followups: 'orders:page:followups',
  packages: 'orders:page:packages',
  print_count_sheet: 'orders:page:count_sheet',
  warehouse: 'orders:page:warehouse',
};

/**
 * The DATA a view is made of, beyond the base read. These three are nothing
 * but money — a balance report, landed cost vs price, carrier bills — and the
 * server refuses their endpoints without `orders:read:costs`, so the route
 * requires it too and the nav row hides without it. Every other view renders
 * its facts from the base read and shows cost or draft detail only when the
 * matching read is held (`can()` at the call site).
 */
export const VIEW_READS: Partial<Record<ViewId, PermissionCode[]>> = {
  today: ['orders:read:costs'],
  balance: ['orders:read:costs'],
  duties: ['orders:read:costs'],
  prices: ['orders:read:costs'],
};

/** Every code a view needs: its page code, then the reads it is made of. */
export function viewCodes(view: ViewId): PermissionCode[] {
  return [VIEW_PERMISSIONS[view], ...(VIEW_READS[view] ?? [])];
}

/** A nav row's id — every ViewId that actually appears in navigation. */
export type NavItemId = Exclude<ViewId, 'shell' | 'print_count_sheet'>;

export interface NavItem {
  /** Matches the view id the workspace renders. */
  id: NavItemId;
  label: string;
  path: string;
  group: NavGroup;
  /** Always `viewCodes(id)` — never written per row (see below). */
  codes: PermissionCode[];
  /** Queue size. Undefined renders no badge (not loaded, or not a queue). */
  count?: (s: QueueSources) => number | undefined;
  /**
   * What the queue holds and how a row leaves it — one or two sentences under
   * the page title. The ONLY place a work view explains itself: the intro
   * paragraphs views used to open with lived inside the card and each said it
   * a different way.
   */
  description?: string;
}

/**
 * Nav rows WITHOUT their permissions: the codes are stamped on below from
 * the page table, so it is impossible to give a row a permission that
 * disagrees with its route.
 */
type NavItemDef = Omit<NavItem, 'codes'>;

const NAV_DEFS: NavItemDef[] = [
  // A top-level page (the shell lists it under Overview), not a work queue.
  {
    id: 'today',
    label: 'Today',
    path: '/today',
    group: 'TOOLS',
  },
  {
    id: 'orders',
    label: 'Journeys',
    path: '/journeys',
    group: 'JOURNEYS',
  },

  // First: what the run explicitly handed you.
  {
    id: 'actions',
    label: 'To decide',
    description:
      'Open asks the runs raised for you, ranked as on Today; answered ones wait for their run.',
    path: '/work/actions',
    group: 'WORK',
    count: ({ work }) => work.actions,
  },
  {
    id: 'to_order',
    label: 'To order',
    description:
      'Every announced release, deadline first. A row with no deadline sorts last and its prep draft never finalises.',
    path: '/work/to-order',
    group: 'WORK',
    count: ({ work }) => work.toOrder,
  },
  {
    id: 'setup',
    label: 'Product setup',
    description:
      'Products the catalog doesn\'t have yet, and vendors without a supplier account, at any stage. Set them up (or run the product import), then Mark set up — the next run re-checks and clears each tag when it finds the real thing.',
    path: '/setup',
    group: 'WORK',
    count: ({ work }) => work.setup,
  },
  // Counted from a separate read: "how many releases are still dark" is only
  // answerable by the live catalog, which the overview must not depend on. The
  // data store reads /orders/releases beside the overview and this badge and
  // the queue's rows come from the same response.
  {
    id: 'releases',
    label: 'Make live',
    description:
      'Releases by street date, with what is still hidden on the shop. Read-only: unhide the products in the acot backend and the counts follow on the next check.',
    path: '/releases',
    group: 'WORK',
    count: ({ releases }) => releasesPending(releases),
  },
  {
    id: 'decide',
    label: 'Discrepancies',
    description:
      'Count differences between what was invoiced and what was received. Each decision routes the row — recount, credit, recapture or let it go — and received products nothing bills get their own ruling below. Recounts stay listed until the count comes back.',
    path: '/work/discrepancies',
    group: 'WORK',
    // Count differences to settle, received-unbilled rows to rule on, and
    // recounts still out with the warehouse — a recount nobody counts blocks
    // costing forever, so it is counted here rather than vanishing.
    count: ({ work }) => work.decide + work.investigate + work.recount,
  },
  {
    id: 'move',
    label: 'Move items',
    description:
      'Products booked onto the wrong receiving. Make each move in the acot backend — the next re-mirror sees the moved products and clears both sides here. Nothing to record in this app.',
    path: '/work/move',
    group: 'WORK',
    count: ({ work }) => work.move,
  },
  {
    id: 'documents',
    label: 'Missing documents',
    description:
      'Receivings missing an invoice or order form. Upload the file yourself, ask the vendor for it, or send the orders run back to hunt the mailbox.',
    path: '/documents',
    group: 'WORK',
    count: ({ work }) => work.documents,
  },
  {
    id: 'balance',
    label: 'Balance receivings',
    description:
      'Receivings ready to balance against their invoice, with the cost edits to apply in the acot grid. Blocked ones say what they still need.',
    path: '/work/balance',
    group: 'WORK',
    count: ({ work }) => work.balance,
  },
  {
    id: 'duties',
    label: 'Duties',
    description:
      'Carrier duty bills that arrived with no shipment, and the receivings holding their balance open for one. Attach a bill to the receiving it cleared, or waive when none is coming.',
    path: '/work/duties',
    group: 'WORK',
    count: ({ work }) => work.duties,
  },
  {
    id: 'prices',
    label: 'Price review',
    description:
      'Landed cost against current selling price for everything received. Price the settled rows once; rows whose receiving is still balancing wait so they are not priced twice.',
    path: '/work/price-check',
    group: 'WORK',
    // The actionable face of the queue: products from the last 60 days'
    // receivings that are flagged and safe to price NOW (settled landed cost;
    // server PRICE_RECENT_DAYS). Older rows are the margin audit, listed in the
    // view — a 2,000+ number here made the whole work column read as noise.
    count: ({ work }) => work.pricesActionable,
  },
  {
    id: 'followups',
    label: 'Follow-ups',
    description:
      'What is in flight: parcels with no journey, delivery problems, credit claims and recounts that need you — and the things someone else finishes, watched until they clear.',
    path: '/work/follow-ups',
    group: 'WORK',
    count: ({ work }) => work.followUpsNeedsYou,
  },

  // No count: delivery problems are Follow-ups rows (that queue owns them),
  // and this row opens the full packages feed — a red "problems" number over a
  // browse-everything view counted the same work twice and promised a queue
  // the click didn't deliver.
  {
    id: 'packages',
    label: 'Packages',
    path: '/packages',
    group: 'TOOLS',
  },
  // The read-only page for warehouse staff: the calendar without its action
  // notes and the discrepancies without their decisions.
  {
    id: 'warehouse',
    label: 'Warehouse',
    path: '/warehouse',
    group: 'TOOLS',
  },
];

/**
 * Every nav row, with its permissions stamped on from the one table above.
 */
export const NAV_ITEMS: NavItem[] = NAV_DEFS.map((def) => ({
  ...def,
  codes: viewCodes(def.id),
}));

/**
 * A nav row's route path, without the leading slash — what react-router wants
 * for a child route. The router builds its paths from here so a nav row and
 * the route it points at cannot come apart either.
 */
export function routePath(id: NavItemId): string {
  const item = NAV_ITEMS.find((i) => i.id === id);
  if (!item) throw new Error(`No nav item ${id}`);
  return item.path.replace(/^\//, '');
}

/** Account-menu pages: under the shell's base read, not in the sidebar. */
export const FETCH_DOMAINS_PATH = '/fetch-domains';

export const NAV_GROUPS: NavGroup[] = ['JOURNEYS', 'WORK', 'TOOLS'];

/** Visible work grouping; route and permission identities remain stable. */
export const WORK_GROUPS: { label: string; ids: NavItemId[] }[] = [
  { label: 'Purchasing', ids: ['to_order', 'setup', 'releases'] },
  { label: 'Receiving & costs', ids: ['decide', 'move', 'documents', 'balance', 'duties', 'prices'] },
  { label: 'Attention', ids: ['actions', 'followups'] },
];

/**
 * Where `/` goes for a user without the Overview page: the first page they
 * hold, in the order the shell presents them (Journeys, then the work column
 * top to bottom, then Packages and Warehouse). Null when they hold no page at all.
 */
export function firstAccessiblePath(can: (code: PermissionCode) => boolean): string | null {
  const ordered: NavItemId[] = ['orders', ...WORK_GROUPS.flatMap((group) => group.ids), 'packages', 'warehouse'];
  for (const id of ordered) {
    const item = NAV_ITEMS.find((i) => i.id === id);
    if (item && item.codes.every(can)) return item.path;
  }
  return null;
}
